/**
 * 参考图处理相关工具函数
 * 处理参考图的上传、规范化等操作
 */

import { uploadBase64ToStorage, isBase64Image } from './uploadBase64'

/** 上传函数类型 */
export type UploadFunction = (files: File[]) => Promise<{ url: string }[] | undefined>

/**
 * 将相对路径的参考图上传到存储
 * @param relativePath 相对路径
 * @param uploadFn 上传函数
 */
export async function uploadReferenceImageToStorage(
  relativePath: string,
  uploadFn: UploadFunction
): Promise<string | undefined> {
  try {
    console.log(`[参考图上传] 检测到相对路径: ${relativePath}`)

    // 1. 添加前导斜杠以从根目录访问
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
    const response = await fetch(path)
    if (!response.ok) {
      console.warn(`[参考图上传] 无法加载参考图: ${relativePath}`)
      return undefined
    }

    // 2. 转换为 File 对象
    const blob = await response.blob()
    const filename = relativePath.split('/').pop() || 'reference.png'
    const file = new File([blob], filename, { type: blob.type || 'image/png' })

    // 3. 上传
    console.log(`[参考图上传] 开始上传: ${filename}`)
    const uploadResult = await uploadFn([file])

    if (uploadResult && uploadResult.length > 0 && uploadResult[0].url) {
      const uploadedUrl = uploadResult[0].url
      console.log(`[参考图上传] 上传成功: ${uploadedUrl}`)
      return uploadedUrl
    }

    console.warn(`[参考图上传] 上传失败，未返回URL`)
    return undefined
  } catch (error) {
    console.error(`[参考图上传] 上传失败: ${relativePath}`, error)
    return undefined
  }
}

/**
 * 规范化并上传图片
 * 如果是 base64，自动上传并返回 URL
 * 如果已经是 URL，直接返回
 */
export async function normalizeAndUploadImage(
  urlOrBase64: string,
  noteId: string,
  imageIndex: number,
  uploadFn: UploadFunction
): Promise<string> {
  if (!urlOrBase64) return ''

  // 如果已经是 HTTP URL，直接返回
  if (urlOrBase64.startsWith('http')) {
    return urlOrBase64
  }

  // 如果是 base64，上传
  if (isBase64Image(urlOrBase64)) {
    const filename = `ai_generated_${noteId}_${imageIndex}_${Date.now()}.png`
    try {
      const uploadedUrl = await uploadBase64ToStorage(urlOrBase64, filename, uploadFn)
      return uploadedUrl
    } catch (error) {
      console.error('Base64 上传失败:', error)
      throw error // 直接抛出错误，不再回退到 Base64 存储
    }
  }

  // 纯 base64 字符串（无前缀）
  return `data:image/png;base64,${urlOrBase64}`
}

/**
 * 规范化并上传参考图
 * 防止 reference_image 字段存储 base64 或相对路径导致数据库膨胀
 * 如果是 base64，自动上传并返回 URL
 * 如果是相对路径，上传并返回 URL
 * 如果已经是 URL，直接返回
 */
export async function normalizeAndUploadReferenceImage(
  urlOrBase64: string | undefined,
  noteId: string,
  imageIndex: number,
  uploadFn: UploadFunction
): Promise<string | undefined> {
  if (!urlOrBase64) return undefined

  // 如果已经是 HTTP URL，直接返回
  if (urlOrBase64.startsWith('http://') || urlOrBase64.startsWith('https://')) {
    return urlOrBase64
  }

  // 如果是 base64，上传
  if (isBase64Image(urlOrBase64)) {
    const filename = `reference_${noteId}_${imageIndex}_${Date.now()}.png`
    try {
      console.log(`🔄 检测到 base64 参考图，开始上传: ${filename}`)
      const uploadedUrl = await uploadBase64ToStorage(urlOrBase64, filename, uploadFn)
      console.log(`✅ 参考图上传成功: ${uploadedUrl}`)
      return uploadedUrl
    } catch (error) {
      console.error('❌ 参考图上传失败，将不存储该参考图:', error)
      return undefined
    }
  }

  // 如果是相对路径（不以 http:// 或 https:// 或 data: 开头），上传
  if (!urlOrBase64.startsWith('data:')) {
    try {
      console.log(`🔄 检测到相对路径参考图，开始上传: ${urlOrBase64}`)
      const uploadedUrl = await uploadReferenceImageToStorage(urlOrBase64, uploadFn)
      if (uploadedUrl) {
        console.log(`✅ 参考图上传成功: ${uploadedUrl}`)
        return uploadedUrl
      }
      console.warn('⚠️ 参考图上传失败，将不存储该参考图')
      return undefined
    } catch (error) {
      console.error('❌ 参考图上传失败:', error)
      return undefined
    }
  }

  // 其他情况，不存储（避免未知格式）
  return undefined
}

/** 参考图解析器配置 */
export interface ResolveReferenceImageOptions {
  /** 参考图 ID（可以是单个或数组） */
  referenceImageId?: string | string[]
  /** 参考图 URL 或路径 */
  referenceImage?: string
  /** 可用的素材列表 */
  assets: Array<{ id: string; url: string }>
  /** 上传函数 */
  uploadFn: UploadFunction
}

/**
 * 解析参考图 URL
 * 支持素材 ID、完整 URL、相对路径
 */
export async function resolveReferenceImageUrl(
  options: ResolveReferenceImageOptions
): Promise<string | undefined> {
  const { referenceImageId, referenceImage, assets, uploadFn } = options

  // 1. 优先通过 reference_image_id 查找素材
  const ids = Array.isArray(referenceImageId)
    ? referenceImageId
    : referenceImageId
      ? [referenceImageId]
      : []

  for (const rid of ids) {
    const asset = assets.find(a => a.id === rid)
    if (asset?.url) return asset.url
  }

  // 2. 处理 reference_image 字段
  if (referenceImage) {
    // 2.1 已经是完整 URL 或 data URI，直接返回
    if (
      referenceImage.startsWith('http://') ||
      referenceImage.startsWith('https://') ||
      referenceImage.startsWith('data:')
    ) {
      return referenceImage
    }
    // 2.2 相对路径，上传
    console.log(`[参考图] 检测到相对路径，正在上传: ${referenceImage}`)
    const uploadedUrl = await uploadReferenceImageToStorage(referenceImage, uploadFn)
    if (uploadedUrl) {
      console.log(`[参考图] 上传成功，URL: ${uploadedUrl}`)
      return uploadedUrl
    }
    console.warn(`[参考图] 上传失败: ${referenceImage}`)
  }

  return undefined
}









